import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Bookmark, BookmarkCheck, Clock, BookOpen, Check, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function Courses() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const { user, refreshUser } = useAuth();

  useEffect(() => {
    http.get("/courses").then(({ data }) =>
      setItems(data.items.sort((a, b) => (a.number || "").localeCompare(b.number || "")))
    );
  }, []);

  const levels = ["All", "Foundation", "Core", "Advanced"];
  const filtered = filter === "All" ? items : items.filter((c) => c.level === filter);

  const toggle = async (slug) => {
    if (!user) { toast.error("Sign in to save modules."); return; }
    const saved = user.saved_courses?.includes(slug);
    try {
      await http.post(saved ? "/me/unsave" : "/me/save", { item_id: slug, kind: "course" });
      await refreshUser();
      toast.success(saved ? "Removed" : "Saved");
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16" data-testid="courses-page">
      <div className="label-eyebrow mb-3">The Curriculum</div>
      <h1 className="text-5xl sm:text-6xl font-black tracking-tight font-display-ai max-w-3xl">
        Seven modules. <span className="text-[#00FFC2]">One ecosystem.</span>
      </h1>
      <p className="text-gray-400 mt-5 max-w-2xl leading-relaxed">
        Each module is built around a deliverable. By the end you've shipped something that earns,
        compounds, or repeats — not just a certificate.
      </p>

      <div className="mt-10 flex flex-wrap gap-2">
        {levels.map((l) => (
          <button
            key={l}
            onClick={() => setFilter(l)}
            data-testid={`course-filter-${l.toLowerCase()}`}
            className={`px-4 py-2 rounded-full text-xs tracking-wide border hairline ${
              filter === l ? "bg-[#00FFC2] text-black border-transparent" : "hover:bg-white/5"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((c) => {
          const isSaved = user?.saved_courses?.includes(c.slug);
          return (
            <article key={c.slug} className="glass rounded-xl overflow-hidden group" data-testid={`course-card-${c.slug}`}>
              <div className="aspect-[16/10] overflow-hidden relative">
                <img src={c.image} alt={c.title} className="w-full h-full object-cover transition duration-700 group-hover:scale-110" />
                <div className="absolute top-3 left-3 glass rounded-md px-2.5 py-1 text-[11px] font-black tracking-widest text-[#00FFC2]">
                  {c.number}
                </div>
                <button
                  onClick={() => toggle(c.slug)}
                  data-testid={`course-save-${c.slug}`}
                  className="absolute top-3 right-3 glass rounded-full p-2"
                  aria-label="Save"
                >
                  {isSaved ? <BookmarkCheck size={16} className="text-[#00FFC2]" /> : <Bookmark size={16} />}
                </button>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 text-[10px] tracking-[0.25em] uppercase text-gray-400 mb-2">
                  <span className="text-[#00FFC2]">{c.level}</span>
                  <span className="inline-flex items-center gap-1"><Clock size={11} />{c.duration_hours}h</span>
                  <span className="inline-flex items-center gap-1"><BookOpen size={11} />{c.lessons} lessons</span>
                </div>
                <div className="text-lg font-semibold">{c.title}</div>
                <div className="text-sm text-gray-400 mt-2">{c.tagline}</div>

                {c.outcomes && (
                  <ul className="mt-4 space-y-1.5">
                    {c.outcomes.slice(0, 3).map((o) => (
                      <li key={o} className="flex items-start gap-2 text-xs text-gray-400">
                        <Check size={12} className="text-[#00FFC2] mt-0.5 flex-shrink-0" />{o}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </article>
          );
        })}
      </div>

      <div className="mt-16 text-center">
        <a
          href="/academy#tiers"
          data-testid="courses-unlock-cta"
          className="btn-mint inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm"
        >
          Unlock all modules <ArrowRight size={16} />
        </a>
      </div>
    </div>
  );
}
